import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import {
  createGuestPublicSkillProfile,
  createPublicSkillClaim,
  createPublicSkillTheme,
  loadPublicSkillAdmin,
  publishAdminPublicSkill,
  publishAdminPublicSkillVersion,
  updateAdminPublicSkill,
  updateAdminPublicSkillProfile,
  updatePublicSkillTheme,
  type PublicSkillAdminSnapshot,
} from "../../lib/public-skills-client";
import { DoorwayBrandIcon } from "../public/DoorwayBrandIcon";

export interface LibraryAdminPanelApi {
  load: typeof loadPublicSkillAdmin;
  createGuestProfile: typeof createGuestPublicSkillProfile;
  createClaim: typeof createPublicSkillClaim;
  createTheme: typeof createPublicSkillTheme;
  updateTheme: typeof updatePublicSkillTheme;
  updateProfile: typeof updateAdminPublicSkillProfile;
  updateSkill: typeof updateAdminPublicSkill;
  publishSkill: typeof publishAdminPublicSkill;
  publishVersion: typeof publishAdminPublicSkillVersion;
}

const defaultApi: LibraryAdminPanelApi = {
  load: loadPublicSkillAdmin,
  createGuestProfile: createGuestPublicSkillProfile,
  createClaim: createPublicSkillClaim,
  createTheme: createPublicSkillTheme,
  updateTheme: updatePublicSkillTheme,
  updateProfile: updateAdminPublicSkillProfile,
  updateSkill: updateAdminPublicSkill,
  publishSkill: publishAdminPublicSkill,
  publishVersion: publishAdminPublicSkillVersion,
};

export function LibraryAdminPanel({ api = defaultApi }: { api?: LibraryAdminPanelApi }) {
  const [snapshot, setSnapshot] = useState<PublicSkillAdminSnapshot | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [claimUrl, setClaimUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const directoryProps = { webkitdirectory: "", directory: "" } as Record<string, string>;

  const refresh = useCallback(async () => {
    const result = await api.load();
    setSnapshot(result);
  }, [api]);

  useEffect(() => {
    void refresh().catch((cause: unknown) => setError(message(cause, "The library controls could not load.")));
  }, [refresh]);

  const mutate = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      await action();
      await refresh();
      setNotice(success);
    } catch (cause) {
      setError(message(cause, "Doorway could not finish that library change."));
    } finally {
      setBusy("");
    }
  };

  if (!snapshot && !error) {
    return <section className="admin-library-panel admin-panel"><div className="library-loading" aria-live="polite"><span /><p>Opening library controls…</p></div></section>;
  }

  const pendingClaims = snapshot?.claims.filter((claim) => claim.status === "pending").length ?? 0;
  const publishedSkills = snapshot?.skills.filter((skill) => skill.status === "published").length ?? 0;
  const downloads = snapshot?.skills.reduce((sum, skill) => sum + Number(skill.download_count), 0) ?? 0;

  return (
    <section className="admin-library-panel" aria-labelledby="admin-library-heading">
      <div className="admin-library-heading">
        <div>
          <p className="eyebrow">Acquisition engine</p>
          <h2 id="admin-library-heading">Public Skills Library</h2>
          <p>Create contributor shelves, publish on an author's behalf, and keep the catalog useful.</p>
        </div>
        <a className="secondary-button" href="/library" target="_blank" rel="noreferrer">Open public library ↗</a>
      </div>

      {error && <p className="library-error" role="alert">{error}</p>}
      {notice && <p className="admin-library-notice" role="status">{notice}</p>}

      <div className="admin-library-metrics">
        <div><strong>{snapshot?.profiles.length ?? 0}</strong><span>Author profiles</span></div>
        <div><strong>{publishedSkills}</strong><span>Published skills</span></div>
        <div><strong>{downloads.toLocaleString()}</strong><span>Total downloads</span></div>
        <div><strong>{pendingClaims}</strong><span>Pending claims</span></div>
      </div>

      <div className="admin-library-grid">
        <article className="admin-panel admin-library-create">
          <div className="admin-panel-heading"><p className="eyebrow">Guest contributor</p><h3>Create an author shelf</h3></div>
          <form onSubmit={(event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            void mutate("guest", () => api.createGuestProfile({
              handle: String(data.get("handle") ?? ""),
              displayName: String(data.get("displayName") ?? ""),
              bio: String(data.get("bio") ?? ""),
            }), "Guest author created.").then(() => form.reset());
          }}>
            <label><span>Name</span><input name="displayName" required /></label>
            <label><span>Handle</span><div className="admin-library-slug"><small>/library/</small><input name="handle" required pattern="[a-z0-9-]+" /></div></label>
            <label><span>Bio</span><textarea name="bio" rows={3} maxLength={500} /></label>
            <button className="primary-button" type="submit" disabled={busy === "guest"}>{busy === "guest" ? "Creating…" : "Create guest profile"}</button>
          </form>
        </article>

        <article className="admin-panel admin-library-create">
          <div className="admin-panel-heading"><p className="eyebrow">Taxonomy</p><h3>Add a theme</h3></div>
          <form onSubmit={(event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            void mutate("theme", () => api.createTheme({
              key: String(data.get("key") ?? ""),
              displayName: String(data.get("displayName") ?? ""),
              description: String(data.get("description") ?? ""),
              sortOrder: Number(data.get("sortOrder") ?? 0),
            }), "Theme added.").then(() => form.reset());
          }}>
            <div className="admin-library-form-row">
              <label><span>Name</span><input name="displayName" required /></label>
              <label><span>Key</span><input name="key" required pattern="[a-z0-9-]+" /></label>
            </div>
            <label><span>Description</span><textarea name="description" rows={2} maxLength={240} /></label>
            <label><span>Order</span><input name="sortOrder" type="number" min={0} max={10000} defaultValue={100} /></label>
            <button className="primary-button" type="submit" disabled={busy === "theme"}>{busy === "theme" ? "Adding…" : "Add theme"}</button>
          </form>
        </article>
      </div>

      <article className="admin-panel admin-library-publish">
        <div className="admin-panel-heading"><p className="eyebrow">Publish for someone</p><h3>Put a skill on any author shelf</h3></div>
        <form onSubmit={(event) => {
          event.preventDefault();
          const form = event.currentTarget;
          const data = new FormData(form);
          void mutate("publish", () => api.publishSkill({
            profileId: String(data.get("profileId") ?? ""),
            slug: String(data.get("slug") ?? ""),
            displayName: String(data.get("displayName") ?? ""),
            summary: String(data.get("summary") ?? ""),
            themeKey: String(data.get("themeKey") ?? ""),
            tags: String(data.get("tags") ?? "").split(",").map((tag) => tag.trim()).filter(Boolean),
            files,
          }), "Skill published to the selected author.").then(() => {
            form.reset();
            setFiles([]);
          });
        }}>
          <div className="admin-library-publish-fields">
            <label><span>Author</span><select name="profileId" required defaultValue=""><option value="" disabled>Choose an author</option>{snapshot?.profiles.map((profile) => <option value={profile.id} key={profile.id}>{profile.displayName} (@{profile.handle})</option>)}</select></label>
            <label><span>Skill name</span><input name="displayName" required maxLength={120} /></label>
            <label><span>Slug</span><input name="slug" required pattern="[a-z0-9-]+" /></label>
            <label><span>Theme</span><select name="themeKey" defaultValue=""><option value="">No theme</option>{snapshot?.themes.filter((theme) => theme.active).map((theme) => <option value={theme.key} key={theme.key}>{theme.name}</option>)}</select></label>
            <label className="admin-library-wide"><span>Summary</span><textarea name="summary" required rows={2} maxLength={280} /></label>
            <label className="admin-library-wide"><span>Tags</span><input name="tags" placeholder="research, positioning" /></label>
          </div>
          <input
            className="visually-hidden"
            type="file"
            multiple
            ref={fileInput}
            {...directoryProps}
            onChange={(event) => setFiles(Array.from(event.currentTarget.files ?? []))}
          />
          <div className="admin-library-folder">
            <DoorwayBrandIcon name="door-outline-green" />
            <span>{files.length ? `${files.length} files selected` : "Choose a skill folder with SKILL.md"}</span>
            <button className="secondary-button" type="button" onClick={() => fileInput.current?.click()}>Choose folder</button>
          </div>
          <button className="primary-button" type="submit" disabled={busy === "publish" || files.length === 0}>{busy === "publish" ? "Checking and publishing…" : "Publish for author"}</button>
        </form>
      </article>

      <div className="admin-library-grid">
        <article className="admin-panel">
          <div className="admin-panel-heading"><p className="eyebrow">Authors</p><h3>Profiles and claims</h3></div>
          <ul className="admin-library-list">
            {snapshot?.profiles.map((profile) => (
              <AdminProfileRow
                api={api}
                busy={busy}
                mutate={mutate}
                profile={profile}
                onClaim={() => {
                  setBusy(`claim-${profile.id}`);
                  setError("");
                  void api.createClaim(profile.id).then((claim) => {
                    setClaimUrl(claim.url);
                    setNotice(`Claim link created for ${profile.displayName}.`);
                    return refresh();
                  }).catch((cause: unknown) => {
                    setError(message(cause, "The claim link could not be created."));
                  }).finally(() => setBusy(""));
                }}
                key={profile.id}
              />
            ))}
          </ul>
          {claimUrl && <label className="admin-library-claim-result"><span>One-use claim link</span><input value={claimUrl} readOnly onFocus={(event) => event.currentTarget.select()} /></label>}
        </article>

        <article className="admin-panel">
          <div className="admin-panel-heading"><p className="eyebrow">Navigation</p><h3>Themes</h3></div>
          <ul className="admin-library-list admin-library-theme-list">
            {snapshot?.themes.map((theme) => (
              <AdminThemeRow api={api} busy={busy} mutate={mutate} theme={theme} key={theme.key} />
            ))}
          </ul>
        </article>
      </div>

      <article className="admin-panel admin-library-skills">
        <div className="admin-panel-heading"><p className="eyebrow">Catalog</p><h3>Every public skill</h3></div>
        <div className="admin-library-skill-table">
          {snapshot?.skills.map((skill) => (
            <AdminSkillRow
              api={api}
              busy={busy}
              mutate={mutate}
              skill={skill}
              themes={snapshot.themes}
              key={skill.skill_id}
            />
          ))}
        </div>
      </article>
    </section>
  );
}

type AdminMutation = (key: string, action: () => Promise<unknown>, success: string) => Promise<void>;
type AdminProfile = PublicSkillAdminSnapshot["profiles"][number];
type AdminTheme = PublicSkillAdminSnapshot["themes"][number];
type AdminSkill = PublicSkillAdminSnapshot["skills"][number];

function AdminProfileRow({ api, busy, mutate, profile, onClaim }: {
  api: LibraryAdminPanelApi;
  busy: string;
  mutate: AdminMutation;
  profile: AdminProfile;
  onClaim: () => void;
}) {
  return (
    <li className="admin-library-edit-row">
      <span className="admin-list-avatar" aria-hidden="true">{profile.displayName.charAt(0).toUpperCase()}</span>
      <div><strong>{profile.displayName}</strong><span>@{profile.handle} · {profile.accountConnected ? "Account connected" : "Guest profile"}</span></div>
      <div className="admin-library-actions">
        {!profile.accountConnected && profile.status !== "suspended" && (
          <button
            type="button"
            aria-label={`Create claim link for ${profile.displayName}`}
            disabled={busy === `claim-${profile.id}`}
            onClick={onClaim}
          >
            Claim link
          </button>
        )}
        <button
          type="button"
          disabled={busy === `profile-${profile.id}`}
          onClick={() => void mutate(
            `profile-${profile.id}`,
            () => api.updateProfile(profile.id, {
              handle: profile.handle,
              displayName: profile.displayName,
              bio: profile.bio,
              imageUrl: profile.imageUrl,
              websiteUrl: profile.websiteUrl,
              status: profile.status === "suspended"
                ? profile.accountConnected ? "claimed" : "unclaimed"
                : "suspended",
            }),
            profile.status === "suspended" ? "Author restored." : "Author suspended.",
          )}
        >
          {profile.status === "suspended" ? "Restore" : "Suspend"}
        </button>
      </div>
      <details className="admin-library-inline-editor">
        <summary>Edit profile</summary>
        <form onSubmit={(event) => {
          event.preventDefault();
          const body = new FormData(event.currentTarget);
          void mutate(`profile-edit-${profile.id}`, () => api.updateProfile(profile.id, {
            handle: String(body.get("handle") ?? ""),
            displayName: String(body.get("displayName") ?? ""),
            bio: String(body.get("bio") ?? ""),
            imageUrl: String(body.get("imageUrl") ?? "") || null,
            websiteUrl: String(body.get("websiteUrl") ?? "") || null,
            status: String(body.get("status")) as "unclaimed" | "claimed" | "suspended",
          }), "Author profile updated.");
        }}>
          <div className="admin-library-editor-grid">
            <label><span>Name</span><input name="displayName" required defaultValue={profile.displayName} /></label>
            <label><span>Handle</span><input name="handle" required pattern="[a-z0-9-]+" defaultValue={profile.handle} /></label>
            <label className="admin-library-editor-wide"><span>Bio</span><textarea name="bio" rows={2} defaultValue={profile.bio} /></label>
            <label><span>Profile image URL</span><input name="imageUrl" type="url" defaultValue={profile.imageUrl ?? ""} /></label>
            <label><span>Website</span><input name="websiteUrl" type="url" defaultValue={profile.websiteUrl ?? ""} /></label>
            <label><span>Status</span><select name="status" defaultValue={profile.status}>
              {profile.accountConnected && <option value="claimed">Claimed</option>}
              {!profile.accountConnected && <option value="unclaimed">Unclaimed</option>}
              <option value="suspended">Suspended</option>
            </select></label>
          </div>
          <button className="secondary-button" type="submit" disabled={busy === `profile-edit-${profile.id}`}>Save profile</button>
        </form>
      </details>
    </li>
  );
}

function AdminThemeRow({ api, busy, mutate, theme }: {
  api: LibraryAdminPanelApi;
  busy: string;
  mutate: AdminMutation;
  theme: AdminTheme;
}) {
  return (
    <li className="admin-library-edit-row">
      <span className="admin-library-theme-dot" aria-hidden="true" />
      <div><strong>{theme.name}</strong><span>{theme.key} · order {theme.sortOrder}</span></div>
      <button
        type="button"
        disabled={busy === `theme-${theme.key}`}
        onClick={() => void mutate(
          `theme-${theme.key}`,
          () => api.updateTheme(theme.key, {
            displayName: theme.name,
            description: theme.description,
            sortOrder: theme.sortOrder,
            isActive: !theme.active,
          }),
          theme.active ? "Theme hidden." : "Theme restored.",
        )}
      >
        {theme.active ? "Hide" : "Restore"}
      </button>
      <details className="admin-library-inline-editor">
        <summary>Edit theme</summary>
        <form onSubmit={(event) => {
          event.preventDefault();
          const body = new FormData(event.currentTarget);
          void mutate(`theme-edit-${theme.key}`, () => api.updateTheme(theme.key, {
            displayName: String(body.get("displayName") ?? ""),
            description: String(body.get("description") ?? ""),
            sortOrder: Number(body.get("sortOrder") ?? 0),
            isActive: body.get("isActive") === "true",
          }), "Theme updated.");
        }}>
          <div className="admin-library-editor-grid">
            <label><span>Name</span><input name="displayName" required defaultValue={theme.name} /></label>
            <label><span>Order</span><input name="sortOrder" type="number" min={0} defaultValue={theme.sortOrder} /></label>
            <label className="admin-library-editor-wide"><span>Description</span><textarea name="description" rows={2} defaultValue={theme.description} /></label>
            <label><span>Visibility</span><select name="isActive" defaultValue={String(theme.active)}><option value="true">Visible</option><option value="false">Hidden</option></select></label>
          </div>
          <button className="secondary-button" type="submit" disabled={busy === `theme-edit-${theme.key}`}>Save theme</button>
        </form>
      </details>
    </li>
  );
}

function AdminSkillRow({ api, busy, mutate, skill, themes }: {
  api: LibraryAdminPanelApi;
  busy: string;
  mutate: AdminMutation;
  skill: AdminSkill;
  themes: AdminTheme[];
}) {
  const input = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const directoryProps = { webkitdirectory: "", directory: "" } as Record<string, string>;
  return (
    <div className="admin-library-skill-row">
      <span className="admin-list-project-icon" aria-hidden="true"><span /></span>
      <div><a href={`/library/${skill.author_handle}/${skill.slug}`} target="_blank" rel="noreferrer">{skill.display_name}</a><span>@{skill.author_handle} · {skill.theme_key || "No theme"}</span></div>
      <span>{Number(skill.download_count).toLocaleString()} downloads</span>
      <span>{Number(skill.rating_count) ? `${(Number(skill.rating_sum) / Number(skill.rating_count)).toFixed(1)} rating` : "No ratings"}</span>
      <div className="admin-library-actions">
        <button
          type="button"
          aria-label={`${skill.is_featured ? "Unfeature" : "Feature"} ${skill.display_name}`}
          disabled={busy === `feature-${skill.skill_id}`}
          onClick={() => void mutate(
            `feature-${skill.skill_id}`,
            () => api.updateSkill(skill.skill_id, { isFeatured: !skill.is_featured }),
            skill.is_featured ? "Skill removed from featured." : "Skill featured.",
          )}
        >
          {skill.is_featured ? "Unfeature" : "Feature"}
        </button>
        <button
          type="button"
          disabled={busy === `status-${skill.skill_id}`}
          onClick={() => void mutate(
            `status-${skill.skill_id}`,
            () => api.updateSkill(skill.skill_id, { status: skill.status === "hidden" ? "published" : "hidden" }),
            skill.status === "hidden" ? "Skill published." : "Skill hidden.",
          )}
        >
          {skill.status === "hidden" ? "Publish" : "Hide"}
        </button>
      </div>
      <details className="admin-library-inline-editor admin-library-skill-editor">
        <summary>Edit details or publish a new version</summary>
        <form onSubmit={(event) => {
          event.preventDefault();
          const body = new FormData(event.currentTarget);
          void mutate(`skill-edit-${skill.skill_id}`, () => api.updateSkill(skill.skill_id, {
            displayName: String(body.get("displayName") ?? ""),
            summary: String(body.get("summary") ?? ""),
            themeKey: String(body.get("themeKey") ?? "") || null,
            tags: String(body.get("tags") ?? "").split(",").map((tag) => tag.trim()).filter(Boolean),
            status: String(body.get("status")) as "draft" | "published" | "hidden",
            isFeatured: body.get("isFeatured") === "true",
          }), "Skill updated.");
        }}>
          <div className="admin-library-editor-grid">
            <label><span>Name</span><input name="displayName" required defaultValue={skill.display_name} /></label>
            <label><span>Status</span><select name="status" defaultValue={skill.status}><option value="published">Published</option><option value="hidden">Hidden</option><option value="draft">Draft</option></select></label>
            <label className="admin-library-editor-wide"><span>Summary</span><textarea name="summary" required rows={2} defaultValue={skill.summary} /></label>
            <label><span>Theme</span><select name="themeKey" defaultValue={skill.theme_key ?? ""}><option value="">No theme</option>{themes.map((theme) => <option value={theme.key} key={theme.key}>{theme.name}</option>)}</select></label>
            <label><span>Tags</span><input name="tags" defaultValue={parsedTags(skill.tags_json).join(", ")} /></label>
            <label><span>Featured</span><select name="isFeatured" defaultValue={String(Boolean(skill.is_featured))}><option value="false">Standard</option><option value="true">Featured</option></select></label>
          </div>
          <button className="secondary-button" type="submit" disabled={busy === `skill-edit-${skill.skill_id}`}>Save skill</button>
        </form>
        <div className="admin-library-version">
          <input
            className="visually-hidden"
            type="file"
            multiple
            ref={input}
            {...directoryProps}
            onChange={(event) => setFiles(Array.from(event.currentTarget.files ?? []))}
          />
          <div><strong>Replace the downloadable package</strong><span>{files.length ? `${files.length} files selected` : "Choose the complete folder with SKILL.md."}</span></div>
          <button className="secondary-button" type="button" onClick={() => input.current?.click()}>Choose folder</button>
          <button
            className="primary-button"
            type="button"
            disabled={!files.length || busy === `skill-version-${skill.skill_id}`}
            onClick={() => void mutate(
              `skill-version-${skill.skill_id}`,
              () => api.publishVersion(skill.skill_id, files),
              "New skill version published.",
            ).then(() => setFiles([]))}
          >
            Publish version
          </button>
        </div>
      </details>
    </div>
  );
}

function parsedTags(value: string): string[] {
  try {
    const tags = JSON.parse(value);
    return Array.isArray(tags) ? tags.filter((tag): tag is string => typeof tag === "string") : [];
  } catch {
    return [];
  }
}

function message(cause: unknown, fallback: string): string {
  return cause instanceof Error ? cause.message : fallback;
}
